import { changeOutcome } from '../../account/subscription'
import { messageForBillingError } from '../../api/errors'
import type { Household } from '../../api/households'
import type { Subscription, SubscriptionChange } from '../../api/subscriptions'
import { useSettingEditor } from './Setting'
import { useUpdateSubscription } from './useUpdateSubscription'

interface SubscriptionEditorOptions {
  subscription: Subscription
  household: Household
  /** The change to ask for, read when the form is submitted. */
  change(): SubscriptionChange
  validate?(): string | null
  /** A reading of a failure the row understands better; null leaves it to the billing mapping. */
  onError?(cause: unknown): string | null
  /**
   * Where the outcome of a change to the status is kept — Paused, Cancelled,
   * Resumed or Restarted. The row that asked for such a change is replaced
   * once it lands, so it cannot say "Saved" the way any other row does; the
   * rows that survive keep the word instead, and drop it when an editor
   * starts, as "Saved" is dropped. See SubscriptionRows.
   */
  setOutcome(outcome: string | null): void
}

/** A setting editor whose save asks Recharge, through the platform, for one change to a subscription. */
export function useSubscriptionEditor({
  subscription,
  household,
  change,
  validate,
  onError,
  setOutcome,
}: SubscriptionEditorOptions) {
  const update = useUpdateSubscription(household)

  const editor = useSettingEditor({
    save: async () => {
      const asked = change()
      await update.mutateAsync({ subscription, change: asked })
      setOutcome(changeOutcome(subscription, asked))
    },
    validate,
    onError: (cause) => onError?.(cause) ?? messageForBillingError(cause),
  })

  return {
    ...editor,
    start() {
      setOutcome(null)
      editor.start()
    },
  }
}
